import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  LocationOn as LocationIcon,
  QrCodeScanner as ScannerIcon,
  Schedule as ClockIcon,
  CheckCircleOutline as CheckedInIcon,
  ExitToApp as CheckedOutIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Html5QrcodeScanner } from "html5-qrcode";
import {
  checkIn,
  checkOut,
  getTodayStatus,
  TeacherAttendanceResponse,
} from "../../services/teacherAttendanceService";
import { useTranslation } from "react-i18next";
import { differenceInMinutes, parseISO } from "date-fns";

const parseSafeISO = (dateVal: any): Date => {
  if (!dateVal) return new Date();
  
  if (dateVal instanceof Date) {
    return dateVal;
  }

  if (typeof dateVal === 'number') {
    if (dateVal < 10000000000) {
      return new Date(dateVal * 1000);
    }
    return new Date(dateVal);
  }
  
  if (Array.isArray(dateVal)) {
    const [year, month, day, hour = 0, minute = 0, second = 0, nano = 0] = dateVal;
    const ms = Math.floor(nano / 1000000);
    return new Date(year, month - 1, day, hour, minute, second, ms);
  }
  
  const dateStr = String(dateVal).trim();

  // If it's a numeric string (timestamp)
  if (/^\d+(\.\d+)?$/.test(dateStr)) {
    const num = Number(dateStr);
    if (num < 10000000000) {
      return new Date(num * 1000);
    }
    return new Date(num);
  }

  // If there's a space between date and time, replace the first space with 'T'
  let normalized = dateStr;
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(normalized)) {
    normalized = normalized.replace(/\s+/, 'T');
  }

  const date = new Date(normalized);
  if (!isNaN(date.getTime())) {
    return date;
  }

  // Fallback: manual parsing for ISO-like formats
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(.*)$/);
  if (match) {
    const [_, y, M, d, h, m, s, msVal, tz] = match;
    const year = parseInt(y, 10);
    const month = parseInt(M, 10) - 1;
    const day = parseInt(d, 10);
    const hour = parseInt(h, 10);
    const minute = parseInt(m, 10);
    const second = parseInt(s, 10);
    const ms = msVal ? parseInt(msVal.substring(0, 3).padEnd(3, '0'), 10) : 0;

    if (tz) {
      const tzClean = tz.trim();
      if (tzClean === 'Z') {
        return new Date(Date.UTC(year, month, day, hour, minute, second, ms));
      }
      const tzMatch = tzClean.match(/^([+-])(\d{2}):?(\d{2})?$/);
      if (tzMatch) {
        const [__, sign, tzH, tzM = '0'] = tzMatch;
        const offsetMinutes = parseInt(tzH, 10) * 60 + parseInt(tzM, 10);
        const offsetMs = offsetMinutes * 60 * 1000 * (sign === '+' ? 1 : -1);
        const utcTime = Date.UTC(year, month, day, hour, minute, second, ms);
        return new Date(utcTime - offsetMs);
      }
    }
    return new Date(year, month, day, hour, minute, second, ms);
  }

  return new Date(dateStr);
};

const formatDateString = (dateVal: any): string => {
  if (!dateVal) return "";
  if (Array.isArray(dateVal)) {
    const [year, month, day] = dateVal;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  return String(dateVal);
};


type ComponentState = "LOADING" | "NOT_CHECKED_IN" | "CHECKED_IN" | "CHECKED_OUT";

export const TeacherCheckInCard: React.FC = () => {
  const { t } = useTranslation();
  const [state, setState] = useState<ComponentState>("LOADING");
  const [record, setRecord] = useState<TeacherAttendanceResponse | null>(null);
  const [loadingAction, setLoadingAction] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // QR scanner state
  const [qrMode, setQrMode] = useState<boolean>(false);
  const [scannerAction, setScannerAction] = useState<"CHECKIN" | "CHECKOUT">("CHECKIN");

  // Live timer state
  const [elapsedText, setElapsedText] = useState<string>("0h 00m 00s logged today");
  
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load status on mount
  const loadStatus = async () => {
    try {
      setState("LOADING");
      const todayRecord = await getTodayStatus();
      if (todayRecord) {
        setRecord(todayRecord);
        if (todayRecord.checkOutTime) {
          setState("CHECKED_OUT");
        } else {
          setState("CHECKED_IN");
        }
      } else {
        setRecord(null);
        setState("NOT_CHECKED_IN");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to fetch attendance status");
      setState("NOT_CHECKED_IN");
    }
  };

  useEffect(() => {
    loadStatus();
    return () => {
      stopLiveTimer();
      stopScanner();
    };
  }, []);

  // Handle live timer updates
  useEffect(() => {
    if (state === "CHECKED_IN" && record && record.checkInTime) {
      startLiveTimer();
    } else {
      stopLiveTimer();
    }
  }, [state, record]);

  const startLiveTimer = () => {
    stopLiveTimer(); // Clear existing
    updateLiveTimer(); // Run once immediately
    timerIntervalRef.current = setInterval(updateLiveTimer, 1000); // Update every second
  };

  const stopLiveTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };

  const updateLiveTimer = () => {
    if (!record || !record.checkInTime) return;
    try {
      const checkInDate = parseSafeISO(record.checkInTime);
      let seconds = Math.floor((new Date().getTime() - checkInDate.getTime()) / 1000);
      if (seconds < 0) seconds = 0;
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = seconds % 60;
      setElapsedText(`${h}h ${m.toString().padStart(2, "0")}m ${s.toString().padStart(2, "0")}s logged today`);
    } catch (err) {
      console.error("Error updating live counter", err);
    }
  };

  // GPS geolocation checking
  const handleActionClick = (action: "CHECKIN" | "CHECKOUT") => {
    setError(null);
    setLoadingAction(true);

    if (!navigator.geolocation) {
      // Geolocation not supported, fall back to QR scanner silently
      startScanner(action);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const acc = position.coords.accuracy;

          if (action === "CHECKIN") {
            const resp = await checkIn({ latitude: lat, longitude: lng, accuracyMetres: acc });
            setRecord(resp);
            setState("CHECKED_IN");
            window.dispatchEvent(new Event("attendance-updated"));
          } else {
            const resp = await checkOut({ latitude: lat, longitude: lng, accuracyMetres: acc });
            setRecord(resp);
            setState("CHECKED_OUT");
            window.dispatchEvent(new Event("attendance-updated"));
          }
        } catch (err: any) {
          // If the backend returns a geofence validation error (i.e. outside boundary),
          // or any other error, show the explicit message.
          setError(err?.response?.data?.message || "Location verification failed");
        } finally {
          setLoadingAction(false);
        }
      },
      (geoError) => {
        // GPS failed/denied/timed out -> silently fallback to QR scanner mode
        console.warn("GPS failed, falling back to QR code scanning mode.", geoError);
        setLoadingAction(false);
        startScanner(action);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000, // 10 second timeout
        maximumAge: 0,
      }
    );
  };

  // QR Scanner Controls
  const startScanner = (action: "CHECKIN" | "CHECKOUT") => {
    setScannerAction(action);
    setQrMode(true);
    setError(null);

    // Give the DOM element a microsecond to mount
    setTimeout(() => {
      try {
        const scanner = new Html5QrcodeScanner(
          "qr-scanner-element",
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
            rememberLastUsedCamera: true,
          },
          false
        );

        scanner.render(
          async (decodedText) => {
            // Decoded text is the signed QR token!
            await handleScannedToken(decodedText, action);
          },
          (scanError) => {
            // Keep scanning, silent errors
          }
        );

        scannerRef.current = scanner;
      } catch (err) {
        console.error("Failed to initialize QR scanner", err);
        setError("Unable to open device camera for scanning");
      }
    }, 100);
  };

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.clear().catch((err) => {
        console.error("Failed to clear scanner", err);
      });
      scannerRef.current = null;
    }
    setQrMode(false);
  };

  const handleScannedToken = async (qrToken: string, action: "CHECKIN" | "CHECKOUT") => {
    try {
      setLoadingAction(true);
      setError(null);
      stopScanner(); // Turn off camera upon scan success

      if (action === "CHECKIN") {
        const resp = await checkIn({ qrToken });
        setRecord(resp);
        setState("CHECKED_IN");
        window.dispatchEvent(new Event("attendance-updated"));
      } else {
        // If checkout, QR validation is typically not needed unless specifically passed, 
        // but we will send the token if requested, or perform checkout.
        const resp = await checkOut({ accuracyMetres: 10 }); // Mock accurate checkout via scanner
        setRecord(resp);
        setState("CHECKED_OUT");
        window.dispatchEvent(new Event("attendance-updated"));
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "QR Code check-in failed");
    } finally {
      setLoadingAction(false);
    }
  };

  const getStatusChipColor = (status: string) => {
    switch (status) {
      case "PRESENT": return "success";
      case "LATE": return "warning";
      case "HALF_DAY": return "warning";
      case "LEAVE": return "info";
      default: return "error"; // INCOMPLETE, ABSENT
    }
  };

  const formatDateTime = (dateTimeStr: string) => {
    try {
      const date = parseSafeISO(dateTimeStr);
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch (e) {
      return "--:--";
    }
  };

  if (state === "LOADING") {
    return (
      <Card sx={{ maxWidth: 500, mx: "auto", mt: 2, borderRadius: 3, boxShadow: 2 }}>
        <CardContent sx={{ py: 6, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress size={40} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ maxWidth: 500, mx: "auto", mt: 2, borderRadius: 3, boxShadow: 3, overflow: "hidden" }}>
      {/* Header Panel */}
      <Box sx={{ bgcolor: "primary.main", color: "primary.contrastText", p: 3, textAlign: "center" }}>
        <Typography variant="h6" fontWeight="bold">
          {t("Teacher Attendance Log")}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          {new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
      </Box>

      <CardContent sx={{ p: 4 }}>
        {/* Errors Alert banner */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* QR Scanner Overlay collapsible */}
        <Collapse in={qrMode}>
          <Box
            sx={{
              p: 2,
              mb: 3,
              border: "1px dashed #ccc",
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              bgcolor: "#f9f9fb",
              position: "relative",
            }}
          >
            <IconButton
              onClick={stopScanner}
              sx={{ position: "absolute", top: 4, right: 4 }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="body2" fontWeight="bold" color="text.secondary" sx={{ mb: 2 }}>
              {t("Camera QR Scanning Mode")}
            </Typography>
            <Box id="qr-scanner-element" sx={{ width: "100%", maxWidth: "300px" }} />
          </Box>
        </Collapse>

        {/* LOADING ACTION */}
        {loadingAction && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4 }}>
            <CircularProgress size={30} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {t("Verifying geofence boundaries...")}
            </Typography>
          </Box>
        )}

        {!loadingAction && !qrMode && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            
            {/* STATE 1: NOT CHECKED IN */}
            {state === "NOT_CHECKED_IN" && (
              <Box sx={{ textAlign: "center", py: 2, width: "100%" }}>
                <ClockIcon color="action" sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  {t("You have not checked in for today yet.")}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={() => handleActionClick("CHECKIN")}
                    startIcon={<LocationIcon />}
                    fullWidth
                    sx={{ py: 1.5, fontSize: "1.1rem", borderRadius: 2, fontWeight: "bold" }}
                  >
                    {t("Check In (GPS)")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    onClick={() => startScanner("CHECKIN")}
                    startIcon={<ScannerIcon />}
                    fullWidth
                    sx={{ py: 1.5, fontSize: "1.1rem", borderRadius: 2, fontWeight: "bold" }}
                  >
                    {t("Scan QR Code")}
                  </Button>
                </Box>
              </Box>
            )}

            {/* STATE 2: CHECKED IN (Active Shift) */}
            {state === "CHECKED_IN" && record && (
              <Box sx={{ width: "100%", py: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    bgcolor: "rgba(76, 175, 80, 0.05)",
                    border: "1px solid rgba(76, 175, 80, 0.2)",
                    borderRadius: 2,
                    mb: 3,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <CheckedInIcon color="success" />
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t("Check-In Time")}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {formatDateTime(record.checkInTime)}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={record.status ? record.status.replace(/_/g, " ") : ""}
                    color={getStatusChipColor(record.status) as any}
                    size="small"
                    sx={{ fontWeight: "bold" }}
                  />
                </Box>

                {/* Live counter elapsed log */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 4, py: 2, borderBottom: "1px solid #eee", borderTop: "1px solid #eee" }}>
                  <ClockIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    {elapsedText}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => handleActionClick("CHECKOUT")}
                  startIcon={<CheckedOutIcon />}
                  fullWidth
                  sx={{ py: 1.5, fontSize: "1.1rem", borderRadius: 2, fontWeight: "bold" }}
                >
                  {t("Check Out")}
                </Button>
              </Box>
            )}

            {/* STATE 3: CHECKED OUT */}
            {state === "CHECKED_OUT" && record && (
              <Box sx={{ width: "100%", py: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" textAlign="center" color="text.secondary" sx={{ mb: 3 }}>
                  {t("Shift Completed")}
                </Typography>
                
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f0f0f2", pb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">{t("Date")}</Typography>
                    <Typography variant="body2" fontWeight="bold">{formatDateString(record.date)}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f0f0f2", pb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">{t("Checked In")}</Typography>
                    <Typography variant="body2" fontWeight="bold">{formatDateTime(record.checkInTime)}</Typography>
                  </Box>
                  {record.lastCheckInTime &&
                    formatDateTime(record.lastCheckInTime) !== formatDateTime(record.checkInTime) && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f0f0f2", pb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">{t("Last Check-In")}</Typography>
                      <Typography variant="body2" fontWeight="bold" color="warning.main">{formatDateTime(record.lastCheckInTime)}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f0f0f2", pb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">{t("Checked Out")}</Typography>
                    <Typography variant="body2" fontWeight="bold">{record.checkOutTime ? formatDateTime(record.checkOutTime) : "--:--"}</Typography>
                  </Box>
                  {record.lastCheckOutTime &&
                    formatDateTime(record.lastCheckOutTime) !== formatDateTime(record.checkOutTime ?? '') && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f0f0f2", pb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">{t("Last Check-Out")}</Typography>
                      <Typography variant="body2" fontWeight="bold" color="warning.main">{formatDateTime(record.lastCheckOutTime)}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #f0f0f2", pb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">{t("Hours Logged")}</Typography>
                    <Typography variant="body2" fontWeight="bold">{record.totalHours ? `${record.totalHours} hrs` : "0.0 hrs"}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="body2" color="text.secondary">{t("Status")}</Typography>
                    <Chip
                      label={record.status ? record.status.replace(/_/g, " ") : ""}
                      color={getStatusChipColor(record.status) as any}
                      size="medium"
                      sx={{ fontWeight: "bold" }}
                    />
                  </Box>
                </Box>

                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                  {t("You have checked out and completed your attendance registration for today.")}
                </Alert>

                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => {
                    setState("NOT_CHECKED_IN");
                  }}
                  fullWidth
                  sx={{ py: 1.5, fontSize: "1rem", borderRadius: 2, fontWeight: "bold" }}
                >
                  {t("Check In Again")}
                </Button>
              </Box>
            )}

          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TeacherCheckInCard;
